import type { Ring, Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { subFrame } from '../solids';
import { archesOf, buildArchBridge, type ArchBridgeSpec } from '../../terrain/archBridge';
import { CLOISTER_WALK, type CloisterRange } from './cloisterPlan';

interface WallPlan {
  readonly a: Vec2;
  readonly b: Vec2;
  readonly depth: number;
  readonly spec: ArchBridgeSpec;
}

/** Open arcade bays are physical holes, rather than dark slabs on a solid wall. */
function wallPlan(range: CloisterRange): WallPlan {
  const [a, b] = range.front;
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const slots = Math.max(1, Math.floor((length - 1.2) / 3.2));
  const count = range.entrance && slots % 2 === 0 ? slots - 1 : slots;
  const span = Math.min(1.9, (length - 1) / count);
  const pier = count === 1 ? 0.5 : Math.min(1.3, (length - count * span - 1) / (count - 1));
  const spans = Array.from({ length: count }, () => span);
  if (range.entrance) spans[Math.floor(count / 2)] = CLOISTER_WALK.door;
  return {
    a,
    b,
    depth: CLOISTER_WALK.wall,
    spec: {
      length,
      width: CLOISTER_WALK.wall,
      spans,
      pierWidth: pier,
      footY: 0,
      springY: 1.55,
      crownY: 2.8,
      parapet: { height: 0, thickness: 0 },
      deckY: () => 3.55,
    },
  };
}

function wallPoint(plan: WallPlan, x: number, depth: number): Vec2 {
  const dx = (plan.b[0] - plan.a[0]) / plan.spec.length,
    dy = (plan.b[1] - plan.a[1]) / plan.spec.length;
  return [plan.a[0] + dx * x - dy * depth, plan.a[1] + dy * x + dx * depth];
}

export function arcadePiers(range: CloisterRange): Ring[] {
  const p = wallPlan(range),
    arches = archesOf(p.spec);
  const intervals = [0, ...arches.flatMap((a) => [a.left, a.right]), p.spec.length];
  return intervals
    .filter((_, i) => i % 2 === 0 && i + 1 < intervals.length)
    .map((x, i) => {
      const y = intervals[i * 2 + 1]!;
      return [
        wallPoint(p, x, 0),
        wallPoint(p, y, 0),
        wallPoint(p, y, p.depth),
        wallPoint(p, x, p.depth),
      ];
    });
}

export function buildCloisterArcade(
  builder: MeshBuilder,
  wall: VertexStyle,
  frame: Frame,
  range: CloisterRange
): void {
  const p = wallPlan(range),
    centre = wallPoint(p, 0, p.depth / 2);
  const heading = Math.atan2(p.b[1] - p.a[1], p.b[0] - p.a[0]);
  buildArchBridge(builder, wall, subFrame(frame, centre[0], centre[1], heading), p.spec);
}
